import React, { useEffect, useState } from 'react';
import { Gift, ChevronDown, ChevronUp, RefreshCw, Loader2 } from 'lucide-react';
import { vehiclesAPI } from '../services/api';

const Stat = ({ label, value, sub, tone = 'text-gray-900', testId }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-xl font-bold ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-gray-500">{sub}</p>}
  </div>
);

const STATUS = { 'commandé': 'bg-emerald-100 text-emerald-800', 'expiré': 'bg-gray-100 text-gray-500', 'en attente': 'bg-amber-100 text-amber-800' };

// Admin › Commandes: maintenance packs shared with friends → conversions, revenue of friend codes, referrer codes, latest shares
const PackShareStatsCard = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); vehiclesAPI.packShareStats(20).then(setData).catch(() => setData(null)).finally(() => setBusy(false)); };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <section className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-4" data-testid="pack-share-stats">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-bold text-orange-900 inline-flex items-center gap-2"><Gift size={18} className="text-orange-500" /> Packs entretien partagés à des amis</h2>
        <span className="text-xs text-orange-800">code ami -1 % (7 j) · code parrain -1 % (14 j) à la commande de l'ami</span>
        <button type="button" onClick={load} disabled={busy} data-testid="pack-share-stats-refresh" className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-orange-800 bg-white border border-orange-200 hover:bg-orange-100 px-2.5 py-1 rounded-full">{busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Actualiser</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="Packs envoyés" value={data.sent.total} sub={`${data.sent.d7} sur 7 j · ${data.sent.d30} sur 30 j`} testId="pack-share-stat-sent" />
        <Stat label="Amis ayant commandé" value={data.converted.total} sub={`${data.converted.d7} sur 7 j · ${data.converted.d30} sur 30 j`} tone="text-emerald-700" testId="pack-share-stat-converted" />
        <Stat label="Taux de conversion" value={`${data.rate} %`} testId="pack-share-stat-rate" />
        <Stat label="CA codes ami" value={`${Number(data.revenue).toFixed(2)} €`} sub="commandes passées avec un code PACK-AMI" tone="text-orange-700" testId="pack-share-stat-revenue" />
        <Stat label="Codes parrain" value={`${data.referrer_codes.used} / ${data.referrer_codes.issued}`} sub="utilisés / distribués" testId="pack-share-stat-referrer" />
        <Stat label="Amis relancés" value={data.reminded ?? 0} sub="rappel 24 h avant expiration du code" testId="pack-share-stat-reminded" />
      </div>
      {data.recent.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="pack-share-stats-toggle" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-orange-800">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Derniers envois ({data.recent.length})</button>
          {open && (
            <div className="mt-2 overflow-x-auto rounded-lg border border-orange-100 bg-white">
              <table className="w-full text-xs rt-cards" data-testid="pack-share-recent">
                <thead className="bg-orange-50 text-gray-500"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Expéditeur</th><th className="text-left p-2">Ami</th><th className="text-left p-2">Véhicules</th><th className="text-right p-2">Pièces</th><th className="text-left p-2">Code</th><th className="text-left p-2">Statut</th></tr></thead>
                <tbody>
                  {data.recent.map((s) => (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`pack-share-row-${s.id}`}>
                      <td className="p-2 whitespace-nowrap" data-label="Date">{s.created_at.slice(0, 10)}</td>
                      <td className="p-2" data-label="Expéditeur">{s.from}</td>
                      <td className="p-2" data-label="Ami">{s.friend_name ? `${s.friend_name} · ` : ''}{s.to}</td>
                      <td className="p-2" data-label="Véhicules">{s.vehicles.join(' / ')}</td>
                      <td className="p-2 text-right" data-label="Pièces">{s.parts}</td>
                      <td className="p-2 font-mono" data-label="Code">{s.code}{s.referrer_code ? <span className="block text-[10px] text-emerald-700">parrain : {s.referrer_code}</span> : null}</td>
                      <td className="p-2" data-label="Statut"><span className={`px-2 py-0.5 rounded-full ${STATUS[s.status] || ''}`}>{s.status}{s.converted_at ? ` le ${s.converted_at.slice(0, 10)}` : ''}</span>{s.reminded && s.status !== 'commandé' ? <span className="block text-[10px] text-gray-500 mt-0.5">relancé</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default PackShareStatsCard;
